/**
 * Shared types and utilities for the Agenda section (pages-new stack).
 * Ported from:
 *   - src/components/templates/Calendar/Calendar.tsx (CalendarEvent type)
 *   - src/components/templates/CalendarDialog/CalendarDialog.tsx (options + helpers)
 */
import dayjs from 'dayjs'
import {PatientAcknowledgement} from 'src/components-new/feedback/activityAckUtils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The four valid reminder-unit values (milliseconds), matching warningOptions. */
export type WarningUnit = 86400000 | 604800000 | 2592000000 | 31536000000

export type CalendarEvent = {
  _id: string;
  startDate: string;
  endDate: string;
  title: string;
  participants: Record<string, any>[];
  organizer?: Record<string, any>;
  type?: string;
  site?: string;
  shareWithPatient?: boolean;
  link?: string;
  linkTeleconsultation?: string;
  activityStatus?: string;
  channels?: string[];
  reminderMilliseconds?: number;
  warning?: {
    value: number;
    unit: WarningUnit;
  };
  user?: string;
  questionsForDoctor?: string;
  patientNotes?: string;
  // Activity-instance fields (events returned by /user-events/ without a `type`)
  typeCode?: string;
  state?: string;
  beneficiaryId?: string;
  patientAcknowledgement?: PatientAcknowledgement;
}

// ---------------------------------------------------------------------------
// Select / radio options
// ---------------------------------------------------------------------------

/** Options for the reminder unit selector (warning.unit field). */
export const warningOptions = [
  { label: 'Giorni', value: 86400000 },
  { label: 'Settimane', value: 604800000 },
  { label: 'Mesi', value: 2592000000 },
  { label: 'Anni', value: 31536000000 },
]

/** Options for the "Dove" (site) selector. */
export const siteOptions = [
  { label: 'Centro clinico', value: 'CLINICAL_CENTER' },
  { label: 'Laboratorio analisi', value: 'ANALYSIS_LABORATORY' },
  { label: 'Ambulatorio MMG/PLS', value: 'MMG_PLS_CLINIC' },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Converts a total milliseconds reminder value back to a {value, unit} pair
 * suitable for pre-populating the reminder form fields.
 *
 * Returns `{ value: undefined, unit: undefined }` when:
 *   - `reminderMilliseconds` is `undefined` (no reminder set), or
 *   - `reminderMilliseconds` is `0` or negative (corrupted API payload).
 *
 * Unit thresholds (matching warningOptions, strict less-than boundaries;
 * exact boundary values map to the larger unit):
 *   < 604 800 000 ms   → days   (86 400 000 ms)
 *   < 2 592 000 000 ms  → weeks  (604 800 000 ms)
 *   < 31 536 000 000 ms → months (2 592 000 000 ms)
 *   otherwise           → years  (31 536 000 000 ms)
 */
export function computeWarning(reminderMilliseconds: number | undefined): {
  value: number | undefined
  unit: WarningUnit | undefined
} {
  if (reminderMilliseconds === undefined) {
    return { value: undefined, unit: undefined }
  }
  if (reminderMilliseconds <= 0) {
    return { value: undefined, unit: undefined }
  }
  if (reminderMilliseconds < 604800000) {
    return { value: reminderMilliseconds / 86400000, unit: 86400000 }
  }
  if (reminderMilliseconds < 2592000000) {
    return { value: reminderMilliseconds / 604800000, unit: 604800000 }
  }
  if (reminderMilliseconds < 31536000000) {
    return { value: reminderMilliseconds / 2592000000, unit: 2592000000 }
  }
  return { value: reminderMilliseconds / 31536000000, unit: 31536000000 }
}

/**
 * Merges a calendar day (year/month/date taken from `day`) with a clock time
 * (hour/minute taken from `time`) into a single ISO-8601 string.
 *
 * Mirrors the legacy pattern:
 *   values.startDate
 *     .set({ year: values.startDay.year(), month: values.startDay.month(), date: values.startDay.date() })
 *     .toISOString()
 *
 * Returns null if either argument is null or cannot be parsed as a valid date.
 */
export function mergeDateAndTime(day: string | null, time: string | null): string | null {
  if (day === null || time === null) return null
  const dayjsDay = dayjs(day)
  const dayjsTime = dayjs(time)
  if (!dayjsDay.isValid() || !dayjsTime.isValid()) return null
  return dayjsTime
    .set('year', dayjsDay.year())
    .set('month', dayjsDay.month())
    .set('date', dayjsDay.date())
    .toISOString()
}

// ---------------------------------------------------------------------------
// Italian holiday helpers (used to tint Sundays/holidays in the calendar)
// ---------------------------------------------------------------------------

/** [month (0-indexed), day, name] triples for the fixed-date Italian national holidays. */
const FIXED_ITALIAN_HOLIDAYS: [number, number, string][] = [
  [0, 1, 'Capodanno'],
  [0, 6, 'Epifania'],
  [3, 25, 'Festa della Liberazione'],
  [4, 1, 'Festa dei Lavoratori'],
  [5, 2, 'Festa della Repubblica'],
  [7, 15, 'Ferragosto'],
  [10, 1, 'Ognissanti'],
  [11, 8, 'Immacolata Concezione'],
  [11, 25, 'Natale'],
  [11, 26, 'Santo Stefano'],
]

/**
 * Computes the Gregorian date of Easter Sunday for a given year using the
 * Meeus/Jones/Butcher algorithm.
 */
function computeEasterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

/**
 * Returns the Italian display name of the holiday `date` falls on (fixed-date
 * holiday, Pasqua, or Pasquetta), or `null` if `date` is not a holiday.
 */
export function getItalianHolidayName(date: Date): string | null {
  const month = date.getMonth()
  const day = date.getDate()

  const fixed = FIXED_ITALIAN_HOLIDAYS.find(([m, d]) => m === month && d === day)
  if (fixed) return fixed[2]

  const easterSunday = computeEasterSunday(date.getFullYear())
  if (month === easterSunday.getMonth() && day === easterSunday.getDate()) return 'Pasqua'

  const easterMonday = new Date(easterSunday.getFullYear(), easterSunday.getMonth(), easterSunday.getDate() + 1)
  if (month === easterMonday.getMonth() && day === easterMonday.getDate()) return 'Pasquetta'

  return null
}

/**
 * Returns true if `date` is an Italian national holiday (fixed-date holiday,
 * Pasqua, or Pasquetta). Easter Sunday is always a Sunday too, so it's
 * already covered by a separate `day === 0` check at the call sites.
 */
export function isItalianHoliday(date: Date): boolean {
  return getItalianHolidayName(date) !== null
}
